import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import Sponsors from "../components/Sponsors";
import Hoofdpartner from "../components/Hoofdpartner";
import ActiviteitenPartners from "../components/ActiviteitenPartners";
import { IMAGES, BEACHVOLLEY_PARTNERS } from "../data/site";

const PARTNER_TOP_IMAGE = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/zqxh21gm_Partner%201.jpg";
const PARTNER_BENEFITS_IMAGE = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/zsp8161s_Partner%202.jpg";

const benefits = [
  { title: "Zichtbaarheid", text: "Sterke aanwezigheid voor en tijdens Mechelen Beach 2026." },
  { title: "Lokaal verbonden", text: "Toon je verbondenheid met Mechelen en haar bewoners." },
  { title: "Breed bereik", text: "Connect met families, vrienden en bezoekers van het event." },
  { title: "Zomerse beleving", text: "Plaats je merk in een warme, positieve setting." },
];

export default function Partners() {
  return (
    <>
      <Seo
        title="Partners | Mechelen Beach 2026"
        description="Ontdek de partners van Mechelen Beach 2026 — hoofdpartner Vwala, strandbarpartners en Beachvolley partners."
      />

      {/* TOP INTRO — text left, image right (consistent with other subpages) */}
      <section className="relative overflow-hidden bg-warm-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="animate-fadeup order-2 lg:order-1">
              <span className="eyebrow">Samenwerkingen</span>
              <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#2b1c10] leading-[1.05]">
                Partners
              </h1>
              <p className="mt-5 text-base sm:text-lg text-[#6b5945] max-w-xl leading-relaxed">
                Mechelen Beach wordt gedragen door samenwerkingen met partners
                en lokale organisaties. Wil je mee zichtbaar zijn tijdens de
                editie van 2026? Neem contact op voor de mogelijkheden.
              </p>
              <div className="mt-8">
                <Link to="/contact" data-testid="partners-intro-cta" className="btn-primary">
                  Word partner
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="relative animate-fadeup order-1 lg:order-2">
              <div className="photo">
                <img
                  src={PARTNER_TOP_IMAGE}
                  alt="Mechelen Beach — partners in beeld"
                  className="w-full h-[320px] sm:h-[460px] object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#fdf8ec] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 sm:space-y-20">
          {/* Hoofdpartner */}
          <div>
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#2b1c10]">
                Hoofdpartner
              </h2>
            </div>
            <div className="mt-10">
              <Hoofdpartner showLabel={false} />
            </div>
          </div>

          {/* Strandbarpartners */}
          <div>
            <div className="text-center max-w-2xl mx-auto">
              <span className="eyebrow">Algemene partners</span>
              <h2 className="font-display mt-5 text-3xl sm:text-4xl font-semibold tracking-tight text-[#2b1c10]">
                Strandbarpartners
              </h2>
              <p className="mt-4 text-base text-[#6b5945] leading-relaxed">
                Dankzij deze partners kan Mechelen Beach elke zomer opnieuw
                openen.
              </p>
            </div>
            <div className="mt-10">
              <Sponsors />
            </div>
          </div>

          {/* Beachvolley partners */}
          {BEACHVOLLEY_PARTNERS.some((p) => !!p.logo) && (
            <div>
              <div className="text-center max-w-2xl mx-auto">
                <span className="eyebrow">Tornooi</span>
                <h2 className="font-display mt-5 text-3xl sm:text-4xl font-semibold tracking-tight text-[#2b1c10]">
                  Beachvolleybalpartners
                </h2>
                <p className="mt-4 text-base text-[#6b5945] leading-relaxed">
                  De partners die het Mechelen Beachvolleybal 2026 mogelijk
                  maken.
                </p>
              </div>

              {/* Hoofdpartner Beachvolley */}
              {BEACHVOLLEY_PARTNERS.filter((p) => p.main && p.logo).map((p) => (
                <div key={p.name} className="mt-10 flex justify-center">
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`partners-page-bv-main-${p.name.toLowerCase().replace(/[^a-z]/g, "")}`}
                    title={p.name}
                    className="group relative w-full max-w-md bg-white rounded-2xl border border-[#ebd9b5] shadow-[0_18px_44px_-18px_rgba(43,28,16,0.22)] px-6 py-8 flex flex-col items-center transition-transform duration-300 hover:-translate-y-0.5"
                  >
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.14em] bg-orange-gradient text-white shadow-md">
                      {p.role}
                    </span>
                    <img
                      src={p.logo}
                      alt={p.name}
                      className="mt-5 max-h-20 sm:max-h-24 w-auto object-contain"
                    />
                  </a>
                </div>
              ))}

              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5">
                {BEACHVOLLEY_PARTNERS.filter((p) => !p.main && p.logo).map((p) => (
                  <a
                    key={p.name}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`partners-page-bv-${p.name.toLowerCase().replace(/[^a-z]/g, "")}`}
                    title={p.name}
                    className="h-28 sm:h-32 rounded-2xl bg-white border border-[#ebd9b5] flex items-center justify-center p-5 transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-14px_rgba(43,28,16,0.25)]"
                  >
                    <img
                      src={p.logo}
                      alt={p.name}
                      className={`object-contain ${p.small ? "max-h-[65%] max-w-[70%]" : "max-h-full max-w-full"}`}
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <ActiviteitenPartners bg="bg-sand-2" />

      <section className="bg-sand-2 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <span className="eyebrow">Word partner</span>
              <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2b1c10] leading-[1.05]">
                Vier voordelen voor jouw merk
              </h2>
              <p className="mt-5 text-base sm:text-lg text-[#6b5945] leading-relaxed">
                Sluit aan bij een lokaal evenement dat duizenden bezoekers
                bereikt — van families tot sporters en jonge professionals.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {benefits.map((b, i) => (
                  <div
                    key={b.title}
                    data-testid={`partner-benefit-${i}`}
                    className="bg-white rounded-xl border border-[#ebd9b5] p-4"
                  >
                    <p className="font-display font-semibold text-[#2b1c10]">{b.title}</p>
                    <p className="mt-1 text-xs text-[#6b5945] leading-relaxed">{b.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="photo">
              <img
                src={PARTNER_BENEFITS_IMAGE}
                alt="Mechelen Beach — bezoekers op het terras"
                className="w-full h-[360px] sm:h-[460px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#fdf8ec] py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0">
              <img src={IMAGES.strandbarNight} alt="" className="w-full h-full object-cover" aria-hidden="true" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#2b1c10]/85 to-[#ed6a1a]/40" />
            </div>
            <div className="relative px-6 sm:px-12 py-14 sm:py-20 max-w-2xl">
              <span className="eyebrow-cream">Laat van je horen</span>
              <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.1]">
                Partner worden van Mechelen Beach?
              </h2>
              <p className="mt-5 text-base sm:text-lg text-white/85 leading-relaxed">
                Neem contact op en ontdek de mogelijkheden voor 2026. We
                bekijken graag samen hoe we jouw merk een zomerse boost kunnen
                geven.
              </p>
              <Link to="/contact" data-testid="partners-cta-contact" className="mt-7 btn-primary">
                Contacteer ons
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
